import { useAuth } from "@dl/web-core-js/auth";
import { PageHeader } from "@dl/web-core-js/shell";
import { columns as defineColumns, DataTable } from "@dl/web-core-js/table";
import { Card, cn, Select, Switch } from "@dl/web-core-js/ui";
import { useQuery, type UseQueryResult } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { billedOf, figuresFor } from "../api/figures";
import { SalesGrid } from "../sales/SalesGrid";
import { useSalesRows, useSalesWindows, type GridRow } from "../sales/windows";
import {
  isoDate,
  ReportingClient,
  type Currency,
  type GlobalFX,
  type InvoiceAggregateDTO,
  type OrderAggregateDTO,
  type Period,
  type WindowRequest,
} from "../api/reporting";
import { ClientDrillDown } from "./sales/ClientDrillDown";
import { Movement } from "./sales/figures";
import { ANALYSIS_PRESETS, presetSelection, ratio, resolveColumn, type PeriodColumn } from "./sales/periods";
import { NexpimView } from "../views";

/**
 * Invoiced against ordered, per currency.
 *
 * The pairing is the point: invoiced is money already billed, ordered is money
 * committed but not yet shipped. Either number alone reads as "sales" and
 * misleads, so each tile shows both and the gap between them.
 */
export function HomePage() {
  const { t } = useTranslation();
  const { http } = useAuth();
  const client = useMemo(() => new ReportingClient(http), [http]);

  // The same month, quarter and year the sales analysis uses, compared against
  // the previous stretch cut to the same day. Anything else and the dashboard
  // and the analysis screen would disagree about what "this month" means.
  // The same four periods the analysis page opens on, fixed: this panel is the
  // headline, and a headline that moved with a toggle would not be one.
  const periods = useMemo(() => ANALYSIS_PRESETS.map((preset) => resolveColumn(presetSelection(preset), "previous")), []);
  const summary = useSummaryWindows(client, periods);
  const fxQuery = useQuery({ queryKey: ["globalfx"], queryFn: () => client.globalFx() as Promise<GlobalFX>, staleTime: 60 * 60_000 });

  return (
    <>
      <PageHeader
        title={t("navigation.item.home", "Dashboard")}
        subtitle={t("home.subtitle", "Invoiced and ordered, by currency.")}
      />

      <div className="overflow-auto p-4 lg:p-6">
        <div className="grid gap-4 xl:grid-cols-2">
          <CurrencyTotals summary={summary} currency="CAD" columns={periods} accent="bg-accent" />
          <CurrencyTotals summary={summary} currency="USD" columns={periods} accent="bg-info" />
        </div>

        <div className="mt-6 grid gap-4 xl:grid-cols-2">
          <ClientSalesBoard client={client} fx={fxQuery.data} />
          <ProductSalesBoard client={client} />
        </div>
      </div>
    </>
  );
}

/* ------------------------------------------------------- sales boards --- */

type WindowKey = "LAST_7" | "LAST_30" | "MONTH" | "QUARTER" | "YTD";

const WINDOW_KEYS: WindowKey[] = ["LAST_7", "LAST_30", "MONTH", "QUARTER", "YTD"];

/**
 * A window and what it should be compared against.
 *
 * The comparison is always the equivalent previous stretch — last 30 days
 * against the 30 before that, this quarter against the same span of the last
 * one — so a mid-period number is never measured against a complete one.
 */
function windowPeriods(key: WindowKey, today = new Date()): { period: Period; compare: Period } {
  const daysAgo = (days: number) => {
    const date = new Date(today);
    date.setDate(date.getDate() - days);
    return date;
  };
  const monthRange = (yearOffset: number, monthOffset: number): Period => {
    const start = new Date(today.getFullYear() - yearOffset, today.getMonth() - monthOffset, 1);
    const end = new Date(start.getFullYear(), start.getMonth() + 1, 0);
    return { from: isoDate(start), to: isoDate(end) };
  };
  const quarterRange = (quarterOffset: number): Period => {
    const quarter = Math.floor(today.getMonth() / 3) - quarterOffset;
    const start = new Date(today.getFullYear(), quarter * 3, 1);
    const end = new Date(start.getFullYear(), start.getMonth() + 3, 0);
    return { from: isoDate(start), to: isoDate(end) };
  };

  switch (key) {
    case "LAST_7":
      return {
        period: { from: isoDate(daysAgo(7)), to: isoDate(today) },
        compare: { from: isoDate(daysAgo(14)), to: isoDate(daysAgo(7)) },
      };
    case "LAST_30":
      return {
        period: { from: isoDate(daysAgo(30)), to: isoDate(today) },
        compare: { from: isoDate(daysAgo(60)), to: isoDate(daysAgo(30)) },
      };
    case "MONTH":
      return { period: monthRange(0, 0), compare: monthRange(1, 0) };
    case "QUARTER":
      return { period: quarterRange(0), compare: quarterRange(1) };
    case "YTD":
    default: {
      const year = today.getFullYear();
      const lastYearSameDay = new Date(today);
      lastYearSameDay.setFullYear(year - 1);
      return {
        period: { from: `${year}-01-01`, to: isoDate(today) },
        compare: { from: `${year - 1}-01-01`, to: isoDate(lastYearSameDay) },
      };
    }
  }
}

/**
 * Who is buying, over one period from the strip, with the same grid, cell and
 * drill-down as the sales analysis page — only ever one period at a time here.
 * The strip and the two comparison toggles sit in the grid's own toolbar and
 * affect this board alone; the currency summary above keeps its fixed three.
 */
function ClientSalesBoard({ client, fx }: { client: ReportingClient; fx: GlobalFX | undefined }) {
  const { t } = useTranslation();
  const windows = useSalesWindows({ mode: "single", defaults: ["this-month"] });
  const [drill, setDrill] = useState<GridRow | null>(null);
  const { rows, isLoading } = useSalesRows(client, windows.columns, fx);

  return (
    <section className="flex min-h-96 flex-col overflow-hidden rounded-lg border border-line bg-surface">
      <header className="flex items-center gap-3 border-b border-line px-3 py-2">
        <h3 className="text-[14px] font-semibold text-ink">{t("home.clients", "Client sales")}</h3>
      </header>
      <SalesGrid
        rows={rows}
        columns={windows.columns}
        loading={isLoading}
        stateKey="/home/clients"
        selected={windows.selected}
        onToggle={windows.toggle}
        onRemove={windows.remove}
        single
        comparison={windows.comparison}
        onComparison={windows.setComparison}
        onDrill={setDrill}
      />
      {drill && <ClientDrillDown client={drill} columns={windows.columns} onClose={() => setDrill(null)} />}
    </section>
  );
}

interface ProductRow {
  id: number;
  no: string;
  description: string;
  cadGross: number;
  cadUnits: number;
  usdGross: number;
  usdUnits: number;
}

/** What is selling, split by the currency it sold in. */
function ProductSalesBoard({ client }: { client: ReportingClient }) {
  const { t } = useTranslation();
  const [windowKey, setWindowKey] = useState<WindowKey>("YTD");
  const { period } = useMemo(() => windowPeriods(windowKey), [windowKey]);

  const query = useQuery({
    queryKey: ["home", "product-sales", period.from, period.to],
    queryFn: async () => {
      const [cad, usd] = await Promise.all([
        client.invoicedForCurrency("CAD", period),
        client.invoicedForCurrency("USD", period),
      ]);

      const rows = new Map<number, ProductRow>();
      // order_type 1 is a sale; refunds and adjustments are excluded so this
      // reads as "what shipped", not net movement.
      const collect = (aggregate: InvoiceAggregateDTO, currency: Currency) => {
        for (const part of aggregate.parts ?? []) {
          if (part.totals?.order_type !== 1) continue;
          const id = part.part?.id;
          if (id === undefined) continue;
          const row =
            rows.get(id) ??
            rows
              .set(id, {
                id,
                no: part.part?.no ?? "",
                description: part.part?.description1 ?? "",
                cadGross: 0,
                cadUnits: 0,
                usdGross: 0,
                usdUnits: 0,
              })
              .get(id)!;
          const gross = billedOf(part.totals);
          if (currency === "CAD") {
            row.cadGross += gross;
            row.cadUnits += part.totals?.quantity ?? 0;
          } else {
            row.usdGross += gross;
            row.usdUnits += part.totals?.quantity ?? 0;
          }
        }
      };

      collect(cad, "CAD");
      collect(usd, "USD");
      return [...rows.values()];
    },
    staleTime: 5 * 60_000,
  });

  const cols = useMemo(() => {
    const c = defineColumns<ProductRow>();
    const moneyCell = (gross: number, units: number) => (
      <span className="flex flex-col leading-tight">
        <span className="tabular-nums">{withCommas(gross)}</span>
        <span className="text-[11px] text-ink-muted tabular-nums">{units}</span>
      </span>
    );
    return [
      c.custom(
        "product",
        (row) => row.no,
        (row) => (
          <span className="flex flex-col leading-tight">
            <span className="text-ink">{row.no}</span>
            <span className="truncate text-[11px] text-ink-muted">{row.description}</span>
          </span>
        ),
        { headerKey: "column.product", priority: 1 },
      ),
      c.custom(
        "sales_cad",
        (row) => row.cadGross,
        (row) => moneyCell(row.cadGross, row.cadUnits),
        { headerKey: "column.sales-cad", filter: "range", align: "right", priority: 2 },
      ),
      c.custom(
        "sales_usd",
        (row) => row.usdGross,
        (row) => moneyCell(row.usdGross, row.usdUnits),
        { headerKey: "column.sales-usd", filter: "range", align: "right", priority: 2 },
      ),
      c.number("units", (row) => row.cadUnits + row.usdUnits, {
        headerKey: "column.units",
        filter: "range",
        priority: 3,
      }),
    ];
  }, []);

  return (
    <Board
      title={t("value.title.product-sales", "Product sales")}
      windowKey={windowKey}
      onWindowChange={setWindowKey}
    >
      <DataTable<ProductRow>
        data={query.data ?? []}
        columns={cols}
        getRowId={(row) => String(row.id)}
        loading={query.isLoading}
        initialSorting={[{ id: "units", desc: true }]}
      />
    </Board>
  );
}

function Board({
  title,
  windowKey,
  onWindowChange,
  children,
}: {
  title: string;
  windowKey: WindowKey;
  onWindowChange: (key: WindowKey) => void;
  children: React.ReactNode;
}) {
  const { t } = useTranslation();
  const labels: Record<WindowKey, string> = {
    LAST_7: t("home.window.last-7", "Last 7 days"),
    LAST_30: t("home.window.last-30", "Last 30 days"),
    MONTH: t("home.window.month", "Current month"),
    QUARTER: t("home.window.quarter", "Current quarter"),
    YTD: t("home.window.ytd", "Year to date"),
  };

  return (
    <section className="flex min-h-96 flex-col overflow-hidden rounded-lg border border-line bg-surface">
      <header className="flex items-center gap-3 border-b border-line px-3 py-2">
        <h3 className="text-[14px] font-semibold text-ink">{title}</h3>
        <Select
          className="ml-auto h-8 w-44"
          value={windowKey}
          options={WINDOW_KEYS.map((key) => ({ value: key, label: labels[key] }))}
          onChange={(event) => onWindowChange(event.target.value as WindowKey)}
        />
      </header>
      {children}
    </section>
  );
}

const COMMAS = new Intl.NumberFormat(undefined, {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

function withCommas(value: number): string {
  return COMMAS.format(value);
}





/* --------------------------------------------------- currency totals --- */

/** The headline's figures, per window key: invoiced and ordered, one aggregate per currency. */
interface SummaryWindows {
  invoiced: Map<string, InvoiceAggregateDTO[]>;
  ordered: Map<string, OrderAggregateDTO[]>;
}

const spanOf = (period: Period) => `${period.from}:${period.to}`;

/**
 * Every window the headline needs, in two requests fired together — invoiced
 * and ordered.
 *
 * The panel used to ride on the sales grid's batch, which answers every
 * client of every window with its parts and margin, one window after the
 * other, and gated on all of it to show sixteen numbers. These endpoints
 * answer per currency only, a cheap query a window. Asked once per distinct
 * span, so two columns on the same dates cost one.
 */
function useSummaryWindows(client: ReportingClient, columns: PeriodColumn[]): UseQueryResult<SummaryWindows> {
  const windows = useMemo(() => columns.flatMap((column) => [column.current, column.compare]), [columns]);
  const requests = useMemo(() => {
    const unique = new Map<string, WindowRequest>();
    for (const window of windows) {
      const span = spanOf(window.period);
      unique.set(span, { key: span, from: window.period.from, to: window.period.to });
    }
    return [...unique.values()];
  }, [windows]);

  return useQuery({
    queryKey: ["home", "summary", ...requests.map((request) => request.key)],
    queryFn: async () => {
      const [invoiced, ordered] = await Promise.all([
        client.aggregateWindowsByCurrency(requests),
        client.bookingsForWindowsByCurrency(requests),
      ]);
      const invoicedBySpan = new Map(invoiced.map((window) => [window.key, window.by_currency ?? []]));
      const orderedBySpan = new Map(ordered.map((window) => [window.key, window.by_currency ?? []]));
      const out: SummaryWindows = { invoiced: new Map(), ordered: new Map() };
      for (const window of windows) {
        const span = spanOf(window.period);
        out.invoiced.set(window.key, invoicedBySpan.get(span) ?? []);
        out.ordered.set(window.key, orderedBySpan.get(span) ?? []);
      }
      return out;
    },
    staleTime: 5 * 60_000,
  });
}

/**
 * One currency, three periods, invoiced against ordered.
 *
 * The old dashboard showed four tiles: this month and year to date, each split
 * by currency, each carrying a money total with no sense of whether it was
 * good. The sales analysis banner had the missing half, a movement against the
 * previous period, but neither showed units and neither separated invoiced from
 * ordered, which is the distinction the dashboard existed to make.
 *
 * This is both: money and units, invoiced and ordered, each against the same
 * stretch of the previous period. Margin is deliberately absent. It belongs on
 * the analysis screen, where there is room to explain it.
 */
function CurrencyTotals({
  summary,
  currency,
  columns,
  accent,
}: {
  summary: UseQueryResult<SummaryWindows>;
  currency: Currency;
  columns: PeriodColumn[];
  accent: string;
}) {
  const { t } = useTranslation();
  const { hasView } = useAuth();
  // The panel's own switch, not the grid's: this is the headline, and it
  // should not change because someone below it flipped a mode.
  const [issuedOnly, setIssuedOnly] = useState(false);

  const { isLoading, isError } = summary;

  // Billed, always; the switch narrows it to issued invoices. The grid's
  // basis and scope are not consulted.
  const figuresAt = (key: string) =>
    figuresFor(
      summary.data?.invoiced.get(key)?.find((aggregate) => aggregate.currency === currency),
      issuedOnly ? "issued" : "all",
    );
  const orderedAt = (key: string) => summary.data?.ordered.get(key)?.find((aggregate) => aggregate.currency === currency);

  /** One value and, underneath, its movement. */
  const line = (value: number, previous: number, isMoney: boolean) => (
    <span className="flex flex-col items-end leading-tight">
      <span className="tabular text-lg font-semibold text-ink">{isMoney ? money(value, currency) : units(value)}</span>
      <Movement value={ratio(value, previous)} />
    </span>
  );

  const rows: Array<{ key: string; label: string; cell: (column: PeriodColumn) => React.ReactNode }> = [
    {
      key: "invoiced",
      label: t("home.invoiced", "Invoiced"),
      cell: (column) => line(figuresAt(column.current.key).billed, figuresAt(column.compare.key).billed, true),
    },
    {
      key: "invoiced-units",
      label: t("home.invoiced-units", "Invoiced units"),
      cell: (column) => line(figuresAt(column.current.key).units_billed, figuresAt(column.compare.key).units_billed, false),
    },
    {
      key: "ordered",
      label: t("home.ordered", "Ordered"),
      cell: (column) =>
        line(orderedAt(column.current.key)?.ordered_value ?? 0, orderedAt(column.compare.key)?.ordered_value ?? 0, true),
    },
    {
      key: "ordered-units",
      label: t("home.ordered-units", "Ordered units"),
      cell: (column) =>
        line(orderedAt(column.current.key)?.ordered_quantity ?? 0, orderedAt(column.compare.key)?.ordered_quantity ?? 0, false),
    },
  ];

  return (
    <Card className="relative overflow-hidden py-4 pl-5 pr-4">
      <span aria-hidden="true" className={cn("absolute inset-y-0 left-0 w-1.5", accent)} />

      <div className="mb-3 flex items-start gap-2">
        <div className="flex flex-col leading-tight">
          <h2 className="text-[15px] font-semibold tracking-tight text-ink">
            {t("home.sales-currency", "Sales {{currency}}", { currency })}
          </h2>
          {/* The panel is a summary of the analysis screen, so it says where the
              rest of the answer lives. Hidden without the view, since a link to a
              page that will refuse you is worse than no link. */}
          {hasView(NexpimView.SALES_ANALYSIS) && (
            <Link to="/sales/analysis" className="text-[11px] font-medium text-ink-faint hover:text-accent hover:underline">
              {t("home.view-details", "View details")}
            </Link>
          )}
        </div>
        <Switch
          className="ml-auto"
          checked={issuedOnly}
          onChange={setIssuedOnly}
          label={t("sales.scope.issued", "Issued only")}
        />
      </div>

      {isError ? (
        <p className="text-[13px] text-risk">{t("error.generic", "Could not load")}</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="text-[11px] uppercase tracking-wider text-ink-faint">
                <th className="pb-2 pr-3 text-left font-medium" />
                {columns.map((column) => (
                  <th key={column.key} className="whitespace-nowrap pb-2 pl-3 text-right font-medium">
                    {column.current.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.key} className="border-t border-line-soft">
                  <td className="whitespace-nowrap py-2 pr-3 text-[13px] text-ink-muted">{row.label}</td>
                  {columns.map((column) => (
                    <td key={column.key} className="py-2 pl-3 text-right">
                      {isLoading ? (
                        // A skeleton rather than a dash: a placeholder glyph
                        // reads as a real value of zero at this size.
                        <span className="my-1 block h-5 w-24 animate-pulse rounded bg-surface-sunk" />
                      ) : (
                        row.cell(column)
                      )}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}

function units(value: number): string {
  return new Intl.NumberFormat(undefined, { maximumFractionDigits: 0 }).format(value);
}

function money(value: number, currency: Currency): string {
  return new Intl.NumberFormat(undefined, {
    style: "currency",
    currency,
    maximumFractionDigits: 0,
  }).format(value);
}
